import React, { useEffect, useMemo, useState } from "react";
import Button from "../components/ui/Button";
import "./AdminStatistics.css";

const SECTIONS = ["Panoramica", "Attività", "Percorso clienti"];
const METRICS = {
  clients: "Registrazioni clienti",
  suppliers: "Registrazioni fornitori",
  orders: "Ordini",
  paid: "Ordini pagati",
  value: "Valore ordini pagati",
  cancelled: "Ordini annullati",
  completed: "Ordini completati o consegnati",
};
const STATES = {
  active: "Operativi",
  inactive: "Non operativi",
  pending: "In approvazione",
};
const EVENTS = {
  page_view: "Schermate Web",
  screen_view: "Schermate app",
  registration_started: "Registrazioni avviate",
  registration_result: "Esiti registrazione",
  service_search_completed: "Ricerche servizi",
  supplier_catalog_viewed: "Listini visualizzati",
  add_to_cart_attempt: "Tentativi aggiunta al carrello",
  address_blocked: "Blocchi indirizzo",
  address_flow_result: "Esiti indirizzo",
  add_to_cart: "Aggiunte al carrello",
  view_cart: "Carrelli visualizzati",
  begin_checkout: "Checkout avviati",
  checkout_blocked: "Blocchi checkout",
  payment_initiation_result: "Esiti avvio pagamento",
  checkout_returned: "Ritorni dal pagamento",
  purchase: "Acquisti osservati GA4",
};
const FUNNELS = {
  full: "Dalla schermata osservata all’acquisto",
  purchase: "Dal listino all’acquisto",
  navigation: "Schermata osservata → Ricerca",
  discovery: "Ricerca → Listino",
  catalog: "Listino → Aggiunta",
  cart: "Apertura carrello → Checkout",
  checkout: "Checkout → Pagamento avviato",
  payment: "Pagamento avviato → Acquisto",
  registration: "Registrazione clienti",
};
const STAGES = {
  navigation: ["Navigazione", ["page_view", "screen_view"]],
  registration: [
    "Registrazione",
    ["registration_started", "registration_result"],
  ],
  discovery: [
    "Ricerca e listini",
    ["service_search_completed", "supplier_catalog_viewed"],
  ],
  cart: [
    "Carrello",
    [
      "add_to_cart_attempt",
      "address_blocked",
      "address_flow_result",
      "add_to_cart",
      "view_cart",
    ],
  ],
  checkout: [
    "Checkout e acquisto",
    [
      "begin_checkout",
      "checkout_blocked",
      "payment_initiation_result",
      "checkout_returned",
      "purchase",
    ],
  ],
};
const number = (value) => Number(value || 0).toLocaleString("it-IT");
const euro = (value) =>
  value == null
    ? "Non disponibile"
    : Number.isSafeInteger(Number(value))
    ? (Number(value) / 100).toLocaleString("it-IT", {
        style: "currency",
        currency: "EUR",
      })
    : "Importo non rappresentabile";
export function dateToday() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Rome",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}
export function periodFor(days) {
  const end = dateToday();
  return {
    from: new Date(Date.parse(end) - (days - 1) * 86400000)
      .toISOString()
      .slice(0, 10),
    to: end,
  };
}
const defaults = () => ({
  ...periodFor(30),
  city: "",
  status: "",
  supplierState: "",
  platform: "",
});
function useReport(endpoint, params, enabled = true) {
  const query = useMemo(
    () =>
      new URLSearchParams(
        Object.entries(params).filter(([, v]) => v !== "" && v != null)
      ).toString(),
    [params]
  );
  const [result, setResult] = useState({
      loading: enabled,
      data: null,
      error: "",
    }),
    [retry, setRetry] = useState(0);
  const token = localStorage.getItem("token");
  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    const timeout = setTimeout(() => {
      controller.abort();
      setResult({
        loading: false,
        data: null,
        error: "La richiesta sta impiegando troppo tempo. Riprova.",
      });
    }, 60000);
    setResult({ loading: true, data: null, error: "" });
    fetch(
      `${
        process.env.REACT_APP_API_BASE_URL || ""
      }/api/admin/statistics/${endpoint}?${query}${retry ? "&retry=1" : ""}`,
      {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
        cache: "no-store",
      }
    )
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok)
          throw new Error(data.error || "Statistiche non disponibili");
        return data;
      })
      .then((data) => {
        if (!controller.signal.aborted)
          setResult({ loading: false, data, error: "" });
      })
      .catch((error) => {
        if (!controller.signal.aborted)
          setResult({
            loading: false,
            data: null,
            error: error.message || "Statistiche non disponibili",
          });
      })
      .finally(() => clearTimeout(timeout));
    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [endpoint, query, enabled, retry, token]);
  return { ...result, retry: () => setRetry((n) => n + 1) };
}
function Feedback({ report, children }) {
  if (report.loading) return <p role="status">Caricamento statistiche…</p>;
  if (report.error)
    return (
      <div role="alert">
        <p>{report.error}</p>
        <Button variant="ghost" onClick={report.retry}>
          Riprova
        </Button>
      </div>
    );
  return report.data ? children(report.data) : null;
}
function Change({ current, previous }) {
  if (previous == null) return null;
  const delta = Number(current) - Number(previous);
  return (
    <small>
      {delta === 0 ? "Invariato" : `${delta > 0 ? "+" : ""}${number(delta)}`}{" "}
      rispetto al periodo precedente
    </small>
  );
}
function Table({
  rows,
  columns,
  caption,
  external,
  total,
  page = 1,
  onPage,
  onSort,
  sort = "date",
  direction = "desc",
}) {
  const [localPage, setLocalPage] = useState(1),
    [ordering, setOrdering] = useState({
      key: columns[0].key,
      direction: "asc",
    });
  useEffect(() => setLocalPage(1), [rows]);
  const ordered = external
    ? rows
    : [...rows].sort((a, b) => {
        const av = a[ordering.key],
          bv = b[ordering.key],
          comparison =
            typeof av === "number" && typeof bv === "number"
              ? av - bv
              : String(av ?? "").localeCompare(String(bv ?? ""), "it");
        return ordering.direction === "asc" ? comparison : -comparison;
      });
  const currentPage = external ? page : localPage,
    count = external ? total : rows.length,
    pages = Math.max(1, Math.ceil(count / 25));
  const visible = external
    ? ordered
    : ordered.slice((currentPage - 1) * 25, currentPage * 25);
  if (!count) return <p role="status">Nessun dato per questi filtri.</p>;
  return (
    <>
      <div
        className="stats-table-scroll"
        tabIndex={0}
        role="region"
        aria-label={caption}
      >
        <table>
          <caption>{caption}</caption>
          <thead>
            <tr>
              {columns.map((column) => {
                const active = external
                    ? sort === column.key
                    : ordering.key === column.key,
                  dir = external ? direction : ordering.direction;
                return (
                  <th
                    key={column.key}
                    scope="col"
                    aria-sort={
                      active
                        ? dir === "asc"
                          ? "ascending"
                          : "descending"
                        : "none"
                    }
                  >
                    {!external || column.sortable ? (
                      <button
                        onClick={() => {
                          if (external)
                            onSort(
                              column.key,
                              active && dir === "desc" ? "asc" : "desc"
                            );
                          else
                            setOrdering({
                              key: column.key,
                              direction:
                                active && dir === "asc" ? "desc" : "asc",
                            });
                        }}
                      >
                        {column.label}
                        {active ? (dir === "asc" ? " ↑" : " ↓") : ""}
                      </button>
                    ) : (
                      column.label
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {visible.map((row, i) => (
              <tr key={i}>
                {columns.map((column) => (
                  <td key={column.key}>
                    {column.format
                      ? column.format(row[column.key], row)
                      : row[column.key] ?? "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <nav className="stats-pagination" aria-label={`Pagine ${caption}`}>
          <Button
            variant="ghost"
            disabled={currentPage <= 1}
            onClick={() =>
              external ? onPage(currentPage - 1) : setLocalPage((n) => n - 1)
            }
          >
            Precedente
          </Button>
          <span>
            {currentPage} / {pages}
          </span>
          <Button
            variant="ghost"
            disabled={currentPage >= pages}
            onClick={() =>
              external ? onPage(currentPage + 1) : setLocalPage((n) => n + 1)
            }
          >
            Successiva
          </Button>
        </nav>
      )}
    </>
  );
}
function Trend({ rows, metric, onMetric, ga4 = false }) {
  const choices = ga4
    ? { sessions: "Sessioni", totalUsers: "Utenti osservati" }
    : METRICS;
  const supported = ga4
    ? Object.keys(choices)
    : ["orders", "paid", "value", "clients", "suppliers"];
  const key = metric === "value" ? "value_cents" : metric,
    max = Math.max(1, ...rows.map((r) => Number(r[key] || 0)));
  const points = rows
    .map(
      (r, i) =>
        `${12 + (rows.length === 1 ? 288 : (i * 576) / (rows.length - 1))},${
          152 - (Number(r[key] || 0) * 132) / max
        }`
    )
    .join(" ");
  return (
    <section className="ad-card stats-trend">
      <div className="stats-heading">
        <h2>Andamento</h2>
        <label>
          Metrica
          <select value={metric} onChange={(e) => onMetric(e.target.value)}>
            {supported.map((id) => (
              <option key={id} value={id}>
                {choices[id]}
              </option>
            ))}
          </select>
        </label>
      </div>
      {!rows.length ? (
        <p>Nessun dato nel periodo.</p>
      ) : (
        <>
          <svg
            viewBox="0 0 600 175"
            role="img"
            aria-label={`Andamento ${choices[metric]}. Valori nella tabella seguente.`}
          >
            <line x1="12" y1="153" x2="588" y2="153" className="stats-axis" />
            <polyline points={points} fill="none" className="stats-line" />
            <text x="12" y="12" className="stats-chart-label">
              {metric === "value" ? euro(max) : number(max)}
            </text>
            <text x="12" y="170" className="stats-chart-label">
              0
            </text>
            {rows.length === 1 && (
              <circle
                cx="300"
                cy={152 - (Number(rows[0][key] || 0) * 132) / max}
                r="4"
              />
            )}
          </svg>
          <div className="stats-range">
            <span>{rows[0].date}</span>
            <span>{rows[rows.length - 1].date}</span>
          </div>
          <details>
            <summary>Valori giornalieri</summary>
            <Table
              caption="Andamento giornaliero"
              rows={rows.map((r) => ({
                ...r,
                [key]: metric === "value" ? r[key] : Number(r[key] || 0),
              }))}
              columns={[
                { key: "date", label: "Giorno" },
                {
                  key,
                  label: choices[metric],
                  format: metric === "value" ? euro : number,
                },
              ]}
            />
          </details>
        </>
      )}
    </section>
  );
}
function Overview({ data, onDetails }) {
  const [metric, setMetric] = useState("orders");
  const active = data.suppliers
    .filter((row) => row.state === "active")
    .reduce((sum, row) => sum + row.count, 0);
  const cards = [
    ["clients", data.registrations.clients, data.registrations.previousClients],
    ["active", active, null],
    ["orders", data.current.orders, data.previous.orders],
    ["paid", data.current.paid, data.previous.paid],
    ["value", data.current.value_cents, data.previous.value_cents],
  ];
  return (
    <>
      <div className="stats-kpis">
        {cards.map(([key, value, previous]) => (
          <button
            className="ad-card stats-kpi"
            key={key}
            onClick={() => onDetails(key === "active" ? "suppliers" : key)}
          >
            <span>
              {key === "active" ? "Fornitori operativi oggi" : METRICS[key]}
            </span>
            <strong>{key === "value" ? euro(value) : number(value)}</strong>
            {key === "value" ? (
              <small>Precedente: {euro(previous)}</small>
            ) : (
              <Change current={value} previous={previous} />
            )}
          </button>
        ))}
      </div>
      <Trend rows={data.trend} metric={metric} onMetric={setMetric} />
      <p className="stats-note">
        Fonte: PostgreSQL · Ordini = prenotazioni persistite. Confronto con i{" "}
        {data.trend.length} giorni precedenti.
      </p>
    </>
  );
}
function Activity({ data, filters, setFilters, metric, setMetric, valid }) {
  const [page, setPage] = useState(1),
    [sort, setSort] = useState("date"),
    [direction, setDirection] = useState("desc");
  const isAccount = ["clients", "suppliers"].includes(metric);
  const params = useMemo(
    () => ({
      from: filters.from,
      to: filters.to,
      city: filters.city,
      status: isAccount ? "" : filters.status,
      metric,
      page,
      sort,
      direction,
    }),
    [
      filters.from,
      filters.to,
      filters.city,
      filters.status,
      isAccount,
      metric,
      page,
      sort,
      direction,
    ]
  );
  const report = useReport("details", params, valid);
  useEffect(
    () => setPage(1),
    [filters.from, filters.to, filters.city, filters.status, metric]
  );
  return (
    <>
      <div className="stats-filters">
        <label>
          Approfondimento
          <select value={metric} onChange={(e) => setMetric(e.target.value)}>
            {Object.entries(METRICS).map(([id, label]) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Città
          <select
            value={filters.city}
            onChange={(e) => {
              setPage(1);
              setFilters({ ...filters, city: e.target.value });
            }}
          >
            <option value="">Tutte</option>
            {data.cities.map((city) => (
              <option key={city}>{city}</option>
            ))}
          </select>
        </label>
        {!isAccount && (
          <label>
            Stato ordine
            <select
              value={filters.status}
              onChange={(e) => {
                setPage(1);
                setFilters({ ...filters, status: e.target.value });
              }}
            >
              <option value="">Tutti</option>
              {data.states.map((status) => (
                <option key={status}>{status}</option>
              ))}
            </select>
          </label>
        )}
        {metric === "suppliers" && (
          <label>
            Stato attuale fornitore
            <select
              value={filters.supplierState}
              onChange={(e) =>
                setFilters({ ...filters, supplierState: e.target.value })
              }
            >
              <option value="">Tutti</option>
              {Object.entries(STATES).map(([id, label]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      {metric === "suppliers" && (
        <section className="ad-card">
          <h2>Fornitori per stato e città · oggi</h2>
          <Table
            rows={data.suppliers}
            caption="Situazione attuale fornitori"
            columns={[
              { key: "city", label: "Città" },
              {
                key: "state",
                label: "Stato",
                format: (value) => STATES[value] || value,
              },
              { key: "count", label: "Fornitori", format: number },
            ]}
          />
        </section>
      )}
      <section className="ad-card">
        <h2>{METRICS[metric]}</h2>
        <Feedback report={report}>
          {(details) => (
            <Table
              external
              rows={details.rows}
              total={details.total}
              page={details.page}
              onPage={setPage}
              sort={sort}
              direction={direction}
              onSort={(key, dir) => {
                setSort(key);
                setDirection(dir);
                setPage(1);
              }}
              caption={`${METRICS[metric]} per giorno, città e stato`}
              columns={[
                { key: "date", label: "Giorno", sortable: true },
                { key: "city", label: "Città" },
                ...(isAccount
                  ? [{ key: "city_source", label: "Origine città" }]
                  : []),
                {
                  key: "status",
                  label: isAccount ? "Tipo account" : "Stato",
                  format: (v) =>
                    v === "user"
                      ? "Cliente"
                      : v === "fornitore"
                      ? "Fornitore"
                      : v,
                },
                {
                  key: "count",
                  label: "Numero",
                  sortable: true,
                  format: number,
                },
                ...(!isAccount
                  ? [
                      {
                        key: "value_cents",
                        label: "Valore pagato",
                        sortable: false,
                        format: euro,
                      },
                    ]
                  : []),
              ]}
            />
          )}
        </Feedback>
      </section>
      <p className="stats-note">
        {isAccount
          ? "Registrazioni nel periodo; città attuale dell’account."
          : "Ordini creati nel periodo; città attuale del fornitore. Valore dei soli ordini con pagamento confermato."}
      </p>
      <details className="stats-definitions">
        <summary>Definizioni e limiti dei dati</summary>
        <ul>
          {data.limitations.map((text) => (
            <li key={text}>{text}</li>
          ))}
        </ul>
        <p>
          Pagati: payment_status = paid. Completati: completata o consegnata.
          Annullati: annullata. Stati operativi e pagamento sono indipendenti.
        </p>
      </details>
    </>
  );
}
function Journey({ filters, setFilters, valid }) {
  const [funnel, setFunnel] = useState("purchase"),
    [stage, setStage] = useState("discovery"),
    [metric, setMetric] = useState("sessions"),
    [event, setEvent] = useState("");
  const params = useMemo(
    () => ({
      from: filters.from,
      to: filters.to,
      platform: filters.platform,
      funnel,
    }),
    [filters.from, filters.to, filters.platform, funnel]
  );
  const report = useReport("journey", params, valid);
  return (
    <>
      <details className="ad-card">
        <summary>Filtri avanzati e percorso</summary>
        <div className="stats-filters">
          <label>
            Piattaforma
            <select
              value={filters.platform}
              onChange={(e) =>
                setFilters({ ...filters, platform: e.target.value })
              }
            >
              <option value="">Tutte</option>
              <option value="web">Web</option>
              <option value="android">Android</option>
              <option value="ios">iOS</option>
            </select>
          </label>
          <label>
            Percorso
            <select value={funnel} onChange={(e) => setFunnel(e.target.value)}>
              {Object.entries(FUNNELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </details>
      <Feedback report={report}>
        {(data) => {
          if (["not_configured", "unavailable"].includes(data.status))
            return (
              <div className="ad-card" role="status">
                <p>{data.message}</p>
                {data.missingVariables?.length > 0 && (
                  <p>
                    Variabili backend mancanti:{" "}
                    {data.missingVariables.join(", ")}.
                  </p>
                )}
                <Button variant="ghost" onClick={report.retry}>
                  Riprova
                </Button>
              </div>
            );
          if (data.status === "empty")
            return (
              <p className="ad-card" role="status">
                Nessun utente o sessione osservato da GA4 nel periodo. Prova un
                intervallo diverso.
              </p>
            );
          const current =
              data.summary.find((row) => row.dateRange === "current") || {},
            previous =
              data.summary.find((row) => row.dateRange === "previous") || {};
          const eventRows = Object.entries(EVENTS)
            .map(([eventName, label]) => ({
              eventName,
              label,
              current:
                data.events.find(
                  (r) => r.eventName === eventName && r.dateRange === "current"
                )?.eventCount || 0,
              previous:
                data.events.find(
                  (r) => r.eventName === eventName && r.dateRange === "previous"
                )?.eventCount || 0,
            }))
            .filter((r) => r.current || r.previous);
          const trend = data.trend
            .map((row) => ({
              ...row,
              date: row.date?.replace(/^(\d{4})(\d{2})(\d{2})$/, "$1-$2-$3"),
            }))
            .sort((a, b) => a.date.localeCompare(b.date));
          const funnelColumns = [
            { key: "funnelStepName", label: "Passaggio" },
            { key: "activeUsers", label: "Utenti", format: number },
            {
              key: "funnelStepCompletionRate",
              label: "Proseguimento",
              format: (v) =>
                v == null
                  ? "—"
                  : `${(v * 100).toLocaleString("it-IT", {
                      maximumFractionDigits: 1,
                    })}%`,
            },
            {
              key: "funnelStepAbandonments",
              label: "Senza passaggio successivo (proxy)",
              format: number,
            },
          ];
          return (
            <>
              <p className="stats-note">
                GA4 misura solo gli utenti osservabili con consenso. Gli
                acquisti GA4 restano distinti dagli ordini pagati PostgreSQL.{" "}
                {data.timeZone && `Fuso GA4: ${data.timeZone}.`}
              </p>
              <div className="stats-kpis stats-kpis-two">
                {[
                  ["totalUsers", "Utenti osservati"],
                  ["sessions", "Sessioni osservate"],
                ].map(([key, label]) => (
                  <div className="ad-card stats-kpi" key={key}>
                    <span>{label}</span>
                    <strong>{number(current[key])}</strong>
                    <Change current={current[key]} previous={previous[key]} />
                  </div>
                ))}
              </div>
              <section className="ad-card">
                <h2>Fasi osservate</h2>
                <p className="stats-note">
                  Seleziona una fase. Sono conteggi di eventi ripetibili, senza
                  un ordine obbligatorio.
                </p>
                <div className="stats-stages" aria-label="Fasi del percorso">
                  {Object.entries(STAGES).map(([key, [label, names]]) => {
                    const main = names.includes("purchase")
                      ? "purchase"
                      : names.includes("add_to_cart")
                      ? "add_to_cart"
                      : names[0];
                    const row = eventRows.find((r) => r.eventName === main);
                    return (
                      <button
                        key={key}
                        aria-pressed={stage === key}
                        onClick={() => {
                          setStage(key);
                          setEvent("");
                        }}
                      >
                        <span>{label}</span>
                        <strong>
                          {number(
                            key === "navigation"
                              ? eventRows
                                  .filter((r) => names.includes(r.eventName))
                                  .reduce((sum, r) => sum + r.current, 0)
                              : row?.current
                          )}
                        </strong>
                        <small>
                          {key === "navigation"
                            ? "Schermate osservate"
                            : EVENTS[main]}
                        </small>
                      </button>
                    );
                  })}
                </div>
                <h3>{STAGES[stage][0]}</h3>
                <Table
                  rows={Object.entries(EVENTS)
                    .filter(([name]) => STAGES[stage][1].includes(name))
                    .map(([name, label]) => ({
                      label,
                      current:
                        eventRows.find((r) => r.eventName === name)?.current ||
                        0,
                      previous:
                        eventRows.find((r) => r.eventName === name)?.previous ||
                        0,
                    }))}
                  caption="Eventi della fase"
                  columns={[
                    { key: "label", label: "Evento" },
                    { key: "current", label: "Periodo", format: number },
                    { key: "previous", label: "Precedente", format: number },
                  ]}
                />
                {stage === "navigation" && (
                  <p>
                    Le schermate osservate non identificano il primo ingresso.
                    Home e login condividono la stessa etichetta; la navigazione
                    prima del consenso non è raccolta.
                  </p>
                )}
                {stage === "cart" && (
                  <p>
                    Carrelli unici, tentativi falliti correlati e abbandoni
                    certi: non disponibili. Ogni evento può ripetersi; non
                    sottrarre tentativi e aggiunte.
                  </p>
                )}
                {stage === "checkout" && (
                  <p>
                    Gli esiti di avvio comprendono successi ed errori. Checkout
                    e acquisti GA4 non sono ordini PostgreSQL. Nessuna
                    attribuzione al singolo carrello.
                  </p>
                )}
                {stage === "discovery" && (
                  <>
                    <p>
                      Zero risultati disponibile solo con la dimensione
                      result_count registrata. Listini consultati senza
                      identificativo: conversioni per fornitore non
                      attribuibili.
                    </p>
                    {data.breakdowns?.searchResults ? (
                      <Table
                        rows={data.breakdowns.searchResults}
                        caption="Ricerche per numero di risultati"
                        columns={[
                          {
                            key: "customEvent:result_count",
                            label: "Risultati per ricerca",
                          },
                          {
                            key: "eventCount",
                            label: "Ricerche (eventi)",
                            format: number,
                          },
                          {
                            key: "totalUsers",
                            label: "Utenti per gruppo",
                            format: number,
                          },
                        ]}
                      />
                    ) : (
                      <p>Dettaglio ricerche senza risultati non disponibile.</p>
                    )}
                  </>
                )}
                {stage === "cart" &&
                  (data.breakdowns?.services ? (
                    <Table
                      rows={data.breakdowns.services}
                      caption="Tentativi e aggiunte per servizio"
                      columns={[
                        { key: "customEvent:catalog_id", label: "ID servizio" },
                        {
                          key: "eventName",
                          label: "Evento",
                          format: (v) => EVENTS[v] || v,
                        },
                        { key: "eventCount", label: "Eventi", format: number },
                      ]}
                    />
                  ) : (
                    <p>Dettaglio per servizio non disponibile.</p>
                  ))}
                {["cart", "checkout"].includes(stage) &&
                  (data.breakdowns?.blocks ? (
                    <Table
                      rows={data.breakdowns.blocks.filter(
                        (r) =>
                          r.eventName ===
                          (stage === "cart"
                            ? "address_blocked"
                            : "checkout_blocked")
                      )}
                      caption="Motivi dei blocchi osservati"
                      columns={[
                        { key: "customEvent:error_code", label: "Motivo" },
                        { key: "eventCount", label: "Eventi", format: number },
                      ]}
                    />
                  ) : (
                    <p>
                      Motivi dei blocchi non disponibili. Il codice indirizzo
                      attuale non distingue mancante e incompatibile.
                    </p>
                  ))}
                <details>
                  <summary>Andamento degli eventi della fase</summary>
                  <Table
                    rows={(data.eventTrend || []).filter((r) =>
                      STAGES[stage][1].includes(r.eventName)
                    )}
                    caption="Eventi giornalieri della fase"
                    columns={[
                      { key: "date", label: "Data" },
                      {
                        key: "eventName",
                        label: "Evento",
                        format: (v) => EVENTS[v] || v,
                      },
                      { key: "eventCount", label: "Eventi", format: number },
                    ]}
                  />
                </details>
              </section>
              <details className="ad-card">
                <summary>Utenti e sessioni nel tempo</summary>
                <Trend ga4 rows={trend} metric={metric} onMetric={setMetric} />
              </details>
              <section className="ad-card">
                <h2>Funnel sequenziale · {FUNNELS[funnel]}</h2>
                <p className="stats-note">
                  Funnel chiuso: gli stessi utenti devono completare i passaggi
                  in ordine nel periodo, entro 24 ore dal passaggio precedente.
                  Gli eventi intermedi sono ammessi. Mancato proseguimento è un
                  proxy, non un abbandono certo del carrello. Nessuna
                  attribuzione al singolo listino o servizio.
                </p>
                {data.funnelStatus === "ready" ? (
                  <>
                    <Table
                      rows={data.funnel.filter(
                        (r) => r.dateRange === "current" || !r.dateRange
                      )}
                      columns={funnelColumns}
                      caption="Percorso nel periodo"
                    />
                    <details>
                      <summary>Confronta il periodo precedente</summary>
                      <Table
                        rows={data.funnel.filter(
                          (r) => r.dateRange === "previous"
                        )}
                        columns={funnelColumns}
                        caption="Percorso nel periodo precedente"
                      />
                    </details>
                  </>
                ) : (
                  <p>
                    {data.funnelStatus === "empty"
                      ? "Nessun percorso osservato."
                      : "Funnel non disponibile. I conteggi degli eventi restano consultabili."}
                  </p>
                )}
              </section>
              <details className="ad-card">
                <summary>Eventi osservati e confronto</summary>
                <div className="stats-filters">
                  <label>
                    Tipo di evento
                    <select
                      value={event}
                      onChange={(e) => setEvent(e.target.value)}
                    >
                      <option value="">Tutti gli eventi osservati</option>
                      {eventRows.map((row) => (
                        <option key={row.eventName} value={row.eventName}>
                          {row.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <Table
                  rows={eventRows.filter(
                    (row) => !event || row.eventName === event
                  )}
                  caption="Conteggi eventi GA4"
                  columns={[
                    { key: "label", label: "Evento" },
                    { key: "current", label: "Periodo", format: number },
                    { key: "previous", label: "Precedente", format: number },
                  ]}
                />
                <p>
                  I conteggi degli eventi possono includere ripetizioni e non
                  costituiscono un funnel.
                </p>
              </details>
              <details className="ad-card">
                <summary>Schermate e piattaforme</summary>
                <Table
                  rows={data.screens}
                  caption="Utilizzo schermate"
                  columns={[
                    { key: "screen", label: "Schermata" },
                    { key: "platform", label: "Piattaforma" },
                    {
                      key: "eventCount",
                      label: "Visualizzazioni",
                      format: number,
                    },
                  ]}
                />
                <Table
                  rows={data.platforms}
                  caption="Confronto piattaforme"
                  columns={[
                    { key: "platform", label: "Piattaforma" },
                    { key: "streamId", label: "Stream" },
                    { key: "totalUsers", label: "Utenti", format: number },
                    { key: "sessions", label: "Sessioni", format: number },
                  ]}
                />
              </details>
              <details className="ad-card">
                <summary>Esiti di registrazione e pagamento</summary>
                <Table
                  rows={data.outcomes}
                  caption="Esiti osservati GA4"
                  columns={[
                    {
                      key: "eventName",
                      label: "Evento",
                      format: (v) => EVENTS[v] || v,
                    },
                    { key: "customEvent:outcome", label: "Esito" },
                    { key: "eventCount", label: "Numero", format: number },
                  ]}
                />
              </details>
              {data.limitations.length > 0 && (
                <details className="stats-definitions">
                  <summary>Disponibilità e limiti GA4</summary>
                  <ul>
                    {data.limitations.map((text) => (
                      <li key={text}>{text}</li>
                    ))}
                  </ul>
                </details>
              )}
              <p className="stats-note">
                Aggiornato: {new Date(data.updatedAt).toLocaleString("it-IT")} ·
                Cache fino a 10 minuti. I dati GA4 possono arrivare in ritardo.
              </p>
            </>
          );
        }}
      </Feedback>
    </>
  );
}
export default function AdminStatistics() {
  const [section, setSection] = useState(0),
    [filters, setFilters] = useState(defaults),
    [preset, setPreset] = useState("30"),
    [metric, setMetric] = useState("orders");
  const valid =
    !!filters.from &&
    !!filters.to &&
    filters.from <= filters.to &&
    filters.to <= dateToday() &&
    (Date.parse(filters.to) - Date.parse(filters.from)) / 86400000 < 366;
  const businessParams = useMemo(
    () => ({
      from: filters.from,
      to: filters.to,
      city: filters.city,
      status: filters.status,
      supplierState: filters.supplierState,
    }),
    [
      filters.from,
      filters.to,
      filters.city,
      filters.status,
      filters.supplierState,
    ]
  );
  const business = useReport(
    "business",
    businessParams,
    valid && section !== 2
  );
  return (
    <main className="ad-wrap stats-wrap">
      <div className="ad-page stats-page">
        <h1 className="ad-title">Statistiche</h1>
        <div className="stats-filters stats-period">
          <label>
            Periodo
            <select
              value={preset}
              onChange={(e) => {
                const value = e.target.value;
                setPreset(value);
                if (value !== "custom")
                  setFilters({ ...filters, ...periodFor(Number(value)) });
              }}
            >
              <option value="1">Oggi</option>
              <option value="7">Ultimi 7 giorni</option>
              <option value="30">Ultimi 30 giorni</option>
              <option value="90">Ultimi 90 giorni</option>
              <option value="custom">Intervallo personalizzato</option>
            </select>
          </label>
          {preset === "custom" && (
            <>
              <label>
                Dal
                <input
                  type="date"
                  value={filters.from}
                  max={filters.to || dateToday()}
                  onChange={(e) =>
                    setFilters({ ...filters, from: e.target.value })
                  }
                />
              </label>
              <label>
                Al
                <input
                  type="date"
                  value={filters.to}
                  min={filters.from}
                  max={dateToday()}
                  onChange={(e) =>
                    setFilters({ ...filters, to: e.target.value })
                  }
                />
              </label>
            </>
          )}
          <Button
            variant="ghost"
            onClick={() => {
              setFilters(defaults());
              setPreset("30");
            }}
          >
            Azzera filtri
          </Button>
        </div>
        <p className="stats-note">
          {filters.from} — {filters.to} · Confronto con il periodo precedente
          della stessa durata.
          {filters.to === dateToday() ? " Oggi è ancora in corso." : ""}
        </p>
        <nav className="stats-tabs" aria-label="Sezioni statistiche">
          {SECTIONS.map((label, i) => (
            <button
              key={label}
              aria-current={section === i ? "page" : undefined}
              onClick={() => setSection(i)}
            >
              {label}
            </button>
          ))}
        </nav>
        {!valid ? (
          <p role="alert">
            Scegli un intervallo valido, non futuro, di massimo 366 giorni.
          </p>
        ) : section === 2 ? (
          <Journey filters={filters} setFilters={setFilters} valid={valid} />
        ) : (
          <>
            {(filters.city || filters.status || filters.supplierState) && (
              <p className="stats-note">
                Filtri aziendali:{" "}
                {[filters.city, filters.status, STATES[filters.supplierState]]
                  .filter(Boolean)
                  .join(" · ")}
                . Si applicano alle rispettive metriche.
              </p>
            )}
            <Feedback report={business}>
              {(data) =>
                section === 0 ? (
                  <Overview
                    data={data}
                    onDetails={(value) => {
                      setMetric(value);
                      setSection(1);
                    }}
                  />
                ) : (
                  <Activity
                    data={data}
                    filters={filters}
                    setFilters={setFilters}
                    metric={metric}
                    setMetric={setMetric}
                    valid={valid}
                  />
                )
              }
            </Feedback>
          </>
        )}
      </div>
    </main>
  );
}
